import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from './shared';

const SavingsYieldCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [initial, setInitial] = useState('10.000,00');
  const [monthly, setMonthly] = useState('0,00');
  const [months, setMonths] = useState('12');
  const [monthlyRate, setMonthlyRate] = useState('0,50');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const p0 = parseFloat(parseBrazilianNumber(initial)) || 0;
    const pm = parseFloat(parseBrazilianNumber(monthly)) || 0;
    const n = parseInt(months) || 0;
    const r = (parseFloat(parseBrazilianNumber(monthlyRate)) || 0) / 100;
    if (p0 < 0 || pm < 0 || n <= 0 || r < 0) return;

    let balance = p0;
    for (let i = 0; i < n; i++) {
      balance = balance * (1 + r) + pm;
    }
    const invested = p0 + pm * n;
    const interest = balance - invested;
    const res = { balance, invested, interest };
    setResultState(res);

    onResultChange([
      { label: 'Total Investido', value: `R$ ${invested.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Rendimento', value: `R$ ${interest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Saldo Final', value: `R$ ${balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <FormField label="Depósito Inicial (R$)" value={initial} onChange={setInitial} required isNumeric type="text" />
          <FormField label="Aporte mensal (R$)" value={monthly} onChange={setMonthly} isNumeric type="text" />
          <FormField label="Período (meses)" value={months} onChange={setMonths} required isInteger type="text" step="1" />
          <FormField label="Taxa Mensal (%)" value={monthlyRate} onChange={setMonthlyRate} required isNumeric type="text" helpText="Poupança: aprox. 0.5% + TR" />
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Rendimento
          </button>
        </div>

        {resultState && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResultCard title="Total Investido" value={`R$ ${resultState.invested.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="slate" />
            <ResultCard title="Rendimento" value={`R$ ${resultState.interest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="amber" />
            <ResultCard title="Saldo Final" value={`R$ ${resultState.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="indigo" />
          </div>
        )}
      </div>

      <EducationalBlock
        title="Dicas sobre a Poupança"
        tips={[
          'A poupança é um investimento de baixo risco e alta liquidez',
          'O rendimento ocorre apenas na "data de aniversário" do depósito',
          'Regra atual: 0.5% ao mês + TR (se a Selic estiver acima de 8,5% a.a.)',
          'Lembre-se de que a inflação pode corroer o poder de compra se o rendimento for baixo'
        ]}
      />
    </div>
  );
};

export default SavingsYieldCalculator;
